import type { CompanyEvent } from "../lib/api";
import { formatDate, isIsoDate } from "../lib/format";

const SOURCE_LABELS: Record<string, string> = {
  bodacc: "BODACC",
  registre: "Répertoire des entreprises",
  rne: "INPI – RNE",
  bourse: "Bourse",
  judilibre: "Judilibre",
};

const LINK_LABELS: Record<string, string> = {
  bourse: "voir le cours",
  jugement: "voir la décision sur Judilibre",
  bodacc: "annonce sur bodacc.fr",
};

const DETAIL_LABELS: Record<string, string> = {
  nature: "Nature du jugement",
  date: "Date du jugement",
  complementJugement: "Détail du jugement",
  modifications: "Modification",
  avant: "Avant",
  apres: "Après",
  ajoutes: "Ajoutés",
  retires: "Retirés",
  dateCloture: "Clôture de l'exercice",
  typeDepot: "Type de dépôt",
  descriptif: "Description",
  dateCessationActivitePP: "Cessation d'activité",
  commentaire: "Commentaire",
  dateCommencementActivite: "Début d'activité",
  dateImmatriculation: "Immatriculation",
  categorieVente: "Nature de la vente",
  categorieCreation: "Nature de l'immatriculation",
  opposition: "Oppositions",
  titre: "Journal d'annonces légales",
  numero_annonce: "Numéro d'annonce",
  numero_rg: "Numéro RG",
  chambre: "Chambre",
  solution: "Solution",
  sommaire: "Sommaire",
  extrait: "Extrait",
  recherche: "Trouvé par",
};

// Top-level keys already shown elsewhere or not useful to readers.
const HIDDEN = new Set(["famille", "type_avis", "tribunal", "ville", "champ", "avis_precedent", "type", "texte_url"]);

type Row = [label: string, value: string];

function flatten(value: unknown, key: string, rows: Row[]) {
  if (value === null || value === undefined || value === "" || HIDDEN.has(key)) return;
  if (Array.isArray(value)) {
    if (value.length) rows.push([DETAIL_LABELS[key] ?? key, value.map(String).join(", ")]);
    return;
  }
  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) flatten(v, k, rows);
    return;
  }
  const label = DETAIL_LABELS[key];
  if (!label) return;
  rows.push([label, isIsoDate(value) ? formatDate(value) : String(value)]);
}

export function detailRows(details: Record<string, unknown>): Row[] {
  const rows: Row[] = [];
  flatten(details, "", rows);
  return rows;
}

export function EventTimeline({ events }: { events: CompanyEvent[] }) {
  return (
    <ol className="timeline">
      {events.map((event) => (
        <TimelineItem key={event.id} event={event} />
      ))}
    </ol>
  );
}

function TimelineItem({ event }: { event: CompanyEvent }) {
  const rows = detailRows(event.details);
  const tribunal = typeof event.details.tribunal === "string" ? event.details.tribunal : null;
  // Judgments carry a link to their pseudonymised full text served by the API.
  const fullText = event.kind === "jugement" && typeof event.details.texte_url === "string" ? event.details.texte_url : null;
  const classes = [
    "timeline__item",
    `timeline__item--${event.severity}`,
    event.is_history && "timeline__item--history",
  ].filter(Boolean);

  return (
    <li className={classes.join(" ")}>
      <div className="timeline__date">
        <time dateTime={event.published_on ?? undefined}>{formatDate(event.published_on, "short")}</time>
      </div>
      <div className="timeline__body">
        <span className="timeline__marker" aria-hidden="true" />
        <span className={`tag tag--${event.severity}`}>{event.kind_label}</span>
        <h3 className="timeline__title">{event.title}</h3>
        <div className="timeline__meta">
          {SOURCE_LABELS[event.source] ?? event.source}
          {tribunal && `, ${tribunal}`}
          {event.url && (
            <>
              {", "}
              <a href={event.url} target="_blank" rel="noopener noreferrer">
                {LINK_LABELS[event.kind] ?? LINK_LABELS[event.source] ?? "voir l'annonce officielle"}
              </a>
            </>
          )}
          {event.document_url && (
            <>
              {", "}
              <a href={event.document_url} target="_blank" rel="noopener noreferrer">
                PDF officiel
              </a>
            </>
          )}
          {fullText && (
            <>
              {", "}
              <a href={fullText} target="_blank" rel="noopener noreferrer">
                texte intégral
              </a>
            </>
          )}
        </div>
        {event.is_history && <span className="timeline__history">Publié avant le début de votre suivi</span>}
        {rows.length > 0 && (
          <details className="event-details">
            <summary>Détails</summary>
            <dl>
              {rows.map(([label, value], i) => (
                <div key={i}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </div>
    </li>
  );
}
